import React, { useEffect, useRef } from 'react';
import { isInfected, REVIEWER_INFECTED_MESSAGE } from '../../utils/scanStatus.mjs';

/**
 * The left half of the review walkthrough: the claim under review, read-only.
 *
 * Stays on the same claim for the whole of that claim's review - only the
 * highlighted line item moves, following whichever receipt is open on the right.
 *
 * Intentionally a fresh compact rendering rather than a reuse of expenseForm.mjs /
 * perdiemLodging.mjs: those are 2200+ line editable forms carrying write paths,
 * upload handlers and admin state that have no place in a reviewer's read-only view.
 * The field names below match what those forms read, so the two stay in step.
 */

const fmtCurrency = (amount) => {
  // Sequelize DECIMALs arrive as strings and may carry separators; guard against
  // NaN so a total never renders as "NaN".
  const n = typeof amount === 'number'
    ? amount
    : parseFloat(String(amount ?? '').replace(/[^0-9.-]/g, ''));
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(Number.isFinite(n) ? n : 0);
};

const fmtDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

/**
 * Badge letters for the PTAEs a claim is funded from.
 *
 * A full chart string is 23 characters ("1160682-002.AABHK.54830") and will not
 * fit a per-row column in a nine-column table. So each distinct code gets a
 * letter, the rows carry the letter, and the allocation block below is the
 * legend that spells the codes out once. That is how a set of accounts is
 * normally presented, and it makes "days 1-4 are A, 5-8 are B" readable at a
 * glance - which is the thing the auditor is actually checking.
 *
 * Ordered by first appearance down the rows, so the letters run A, B, C in
 * reading order rather than in whatever order the buckets were created.
 */
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const buildPtaeLegend = (rows) => {
  const order = [];
  const byCode = new Map();

  for (const row of rows) {
    const code = row?.ptae;
    if (!code) continue;
    if (!byCode.has(code)) {
      byCode.set(code, { code, amount: 0, lines: 0, letter: LETTERS[order.length] || '•' });
      order.push(code);
    }
    const entry = byCode.get(code);
    entry.lines += 1;
    entry.amount += Number(row.amount) || 0;
  }

  return { order: order.map(c => byCode.get(c)), byCode };
};

/** Per-row badge. Muted when the row carries no code, so a gap is visible. */
const PtaeBadge = ({ letter }) => (
  letter
    ? (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-stanford-red/10 text-stanford-red text-[10px] font-bold">
        {letter}
      </span>
    )
    : <span className="text-gray-300">-</span>
);

const Field = ({ label, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
    <p className="text-sm text-gray-900 mt-0.5">{children}</p>
  </div>
);

const ClaimHeader = ({ reportClaim, claim, typeLabel }) => (
  <div className="border-b border-gray-200 pb-4 mb-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-lg font-bold text-gray-900 break-words">{reportClaim?.userName || '-'}</h3>
        <p className="text-sm text-gray-500">
          {typeLabel}
          {claim?.user?.position ? ` · ${claim.user.position}` : ''}
          {reportClaim?.userRegion ? ` · ${reportClaim.userRegion}` : ''}
        </p>
      </div>
      <span className="text-xs font-mono text-gray-400 flex-shrink-0">
        #{String(claim?.id || '').slice(-8)}
      </span>
    </div>
  </div>
);

const PerDiemBody = ({ claim, reportClaim, ptaeLegend }) => {
  const details = claim?.dailyDetails || [];
  return (
    <>
      <ClaimHeader reportClaim={reportClaim} claim={claim} typeLabel="Per Diem & Lodging" />

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="Trip Start">{fmtDate(claim?.startDate)}</Field>
        <Field label="Trip End">{fmtDate(claim?.endDate)}</Field>
        <Field label="Total M&IE">{fmtCurrency(claim?.totalMIE)}</Field>
        <Field label="Total Lodging">{fmtCurrency(claim?.totalLodging)}</Field>
      </div>

      <div className="mb-4">
        <Field label="Reason for Trip">
          <span className="whitespace-pre-wrap">{claim?.reasonForTrip || '-'}</span>
        </Field>
      </div>

      {claim?.adminComments && (
        <div className="mb-4 p-3 bg-orange-50 border-l-4 border-orange-400 rounded">
          <p className="text-[11px] uppercase tracking-wide text-orange-700 font-semibold">Admin Comments</p>
          <p className="text-sm text-orange-900 mt-1 whitespace-pre-wrap">{claim.adminComments}</p>
        </div>
      )}

      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
        Daily Breakdown ({details.length} {details.length === 1 ? 'day' : 'days'})
      </p>
      {details.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No daily detail recorded on this claim.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          {/* Below md each day is a compact card (responsive-table plus the
              review-lines grid in index.css); a table from md up. */}
          <table className="responsive-table review-lines w-full text-xs">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                {/* Line numbers so the reviewer can say "day 4" and be
                    understood - the right pane pages documents independently,
                    and a date alone is easy to lose track of on a long trip. */}
                <th className="px-2 py-2 text-right font-semibold w-8">#</th>
                {/* Only when the claim is actually funded line by line -
                    otherwise this would be a column of dashes. */}
                {ptaeLegend.order.length > 0 && (
                  <th className="px-2 py-2 text-center font-semibold w-10">PTA-E</th>
                )}
                <th className="px-2 py-2 text-left font-semibold">Date</th>
                <th className="px-2 py-2 text-left font-semibold">City</th>
                <th className="px-2 py-2 text-right font-semibold">B</th>
                <th className="px-2 py-2 text-right font-semibold">L</th>
                <th className="px-2 py-2 text-right font-semibold">D</th>
                <th className="px-2 py-2 text-right font-semibold">Inc.</th>
                <th className="px-2 py-2 text-right font-semibold">M&IE</th>
                <th className="px-2 py-2 text-right font-semibold">Lodging</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {details.map((d, index) => (
                <tr key={d.id} className={d.adminComments ? 'bg-orange-50' : 'bg-white'}>
                  <td data-label="Day" className="px-2 py-1.5 text-right tabular-nums text-gray-400">
                    {index + 1}
                  </td>
                  {ptaeLegend.order.length > 0 && (
                    <td data-label="PTA-E" className="px-2 py-1.5 text-center">
                      <PtaeBadge letter={ptaeLegend.byCode.get(d.ptae)?.letter} />
                    </td>
                  )}
                  <td data-cell="title" className="px-2 py-1.5 whitespace-nowrap">{fmtDate(d.date)}</td>
                  <td data-label="City" className="px-2 py-1.5">{d.city || '-'}</td>
                  <td data-label="Breakfast" className="px-2 py-1.5 text-right tabular-nums">{fmtCurrency(d.breakfast)}</td>
                  <td data-label="Lunch" className="px-2 py-1.5 text-right tabular-nums">{fmtCurrency(d.lunch)}</td>
                  <td data-label="Dinner" className="px-2 py-1.5 text-right tabular-nums">{fmtCurrency(d.dinner)}</td>
                  <td data-label="Incidentals" className="px-2 py-1.5 text-right tabular-nums">{fmtCurrency(d.incidentals)}</td>
                  <td data-label="M&IE" className="px-2 py-1.5 text-right font-medium tabular-nums">{fmtCurrency(d.totalMIE)}</td>
                  <td data-label="Lodging" className="px-2 py-1.5 text-right tabular-nums">{fmtCurrency(d.lodgingCost)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold text-gray-900">
              <tr>
                <td className="px-2 py-2" colSpan={ptaeLegend.order.length > 0 ? 8 : 7}>
                  Total Claim
                </td>
                <td className="px-2 py-2 text-right tabular-nums" colSpan={2}>{fmtCurrency(claim?.totalClaim)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
};

const ExpenseBody = ({ claim, reportClaim, activeItemId, ptaeLegend, onSelectItem, infectedItemIds }) => {
  const items = claim?.items || [];
  const activeRowRef = useRef(null);

  // Keep the highlighted row visible as the reviewer pages the receipts on the
  // right - on a long claim the active item would otherwise scroll out of view.
  // Below md the claim and its document are shown one at a time (the Details |
  // Documents toggle in MassReviewOverlay), so there is no document beside the
  // pane to keep in step with - and scrolling on open pushed the claimant's
  // name out of the short phone pane. Desktop behaviour is unchanged.
  useEffect(() => {
    const sideBySide = typeof window.matchMedia !== 'function'
      || window.matchMedia('(min-width: 768px)').matches;
    if (activeRowRef.current && sideBySide) {
      activeRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeItemId]);

  return (
    <>
      <ClaimHeader reportClaim={reportClaim} claim={claim} typeLabel="Expense Reimbursement" />

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="Period Start">{fmtDate(claim?.dateRangeStart)}</Field>
        <Field label="Period End">{fmtDate(claim?.dateRangeEnd)}</Field>
        <Field label="Total (USD)">{fmtCurrency(claim?.totalUsdAmount)}</Field>
        <Field label="Line Items">{items.length}</Field>
      </div>

      {claim?.adminComments && (
        <div className="mb-4 p-3 bg-orange-50 border-l-4 border-orange-400 rounded">
          <p className="text-[11px] uppercase tracking-wide text-orange-700 font-semibold">Admin Comments</p>
          <p className="text-sm text-orange-900 mt-1 whitespace-pre-wrap">{claim.adminComments}</p>
        </div>
      )}

      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">Line Items</p>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No line items recorded on this claim.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          {/* Below md each line item is a compact card, description first
              (responsive-table plus the review-lines grid in index.css). */}
          <table className="responsive-table review-lines w-full text-xs">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-2 py-2 text-right font-semibold w-10">#</th>
                {ptaeLegend.order.length > 0 && (
                  <th className="px-2 py-2 text-center font-semibold w-10">PTA-E</th>
                )}
                <th className="px-2 py-2 text-left font-semibold">Date</th>
                <th className="px-2 py-2 text-left font-semibold">Receipt #</th>
                <th className="px-2 py-2 text-left font-semibold">Description</th>
                <th className="px-2 py-2 text-right font-semibold">Amount</th>
                <th className="px-2 py-2 text-right font-semibold">USD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, index) => {
                const isActive = activeItemId && String(item.id) === String(activeItemId);
                // Only a line that actually has a document is worth opening.
                const canOpen = !!onSelectItem && !!item.hasReceipt;
                // The item may carry its own scanStatus, or only its receipt in
                // `supports` does - either one marks the file as removed.
                const receiptInfected = isInfected(item) || !!infectedItemIds?.has(String(item.id));
                return (
                  <tr
                    key={item.id}
                    ref={isActive ? activeRowRef : null}
                    // Click a line to bring up ITS receipt. The pane could only
                    // ever be driven the other way - paging the document strip
                    // highlighted the matching row - so reaching line 12's
                    // receipt meant pressing Next eleven times, which reads as
                    // "I cannot see supporting documents by line item".
                    onClick={canOpen ? () => onSelectItem(item.id) : undefined}
                    title={canOpen ? 'Show the receipt for this line item' : undefined}
                    className={[
                      isActive
                        ? 'bg-stanford-red/10 ring-2 ring-inset ring-stanford-red'
                        : item.adminComments
                          ? 'bg-orange-50'
                          : 'bg-white',
                      canOpen ? 'cursor-pointer hover:bg-stanford-red/5' : '',
                    ].join(' ')}
                  >
                    {/* The number is always shown; the caret marks which line
                        the document on the right belongs to. Sharing one column
                        keeps the count unchanged, so the footer colSpan below
                        stays correct. */}
                    <td
                      data-label="Line"
                      className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${
                        isActive ? 'text-stanford-red font-bold' : 'text-gray-400'
                      }`}
                    >
                      {isActive ? `▶ ${index + 1}` : index + 1}
                    </td>
                    {ptaeLegend.order.length > 0 && (
                      <td data-label="PTA-E" className="px-2 py-1.5 text-center">
                        <PtaeBadge letter={ptaeLegend.byCode.get(item.ptae)?.letter} />
                      </td>
                    )}
                    <td data-label="Date" className="px-2 py-1.5 whitespace-nowrap">{fmtDate(item.expenditureDate)}</td>
                    <td data-label="Receipt #" className="px-2 py-1.5">
                      <span className="flex flex-wrap md:flex-nowrap items-center gap-1">
                        {item.receiptNumber || '-'}
                        {receiptInfected && (
                          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-medium whitespace-nowrap">
                            {REVIEWER_INFECTED_MESSAGE}
                          </span>
                        )}
                        {!receiptInfected && !item.hasReceipt && (
                          <span
                            className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium whitespace-nowrap"
                            title="No receipt document attached to this line item"
                          >
                            no receipt
                          </span>
                        )}
                      </span>
                    </td>
                    <td data-cell="title" className="px-2 py-1.5">
                      {item.expenditureDescription || '-'}
                      {/* No hover on a phone - say that the card opens its receipt. */}
                      {canOpen && !isActive && (
                        <span className="md:hidden block mt-0.5 text-[11px] font-medium text-stanford-red">
                          View receipt ›
                        </span>
                      )}
                    </td>
                    <td data-label="Amount" className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                      {item.expenditureCurrency} {Number(item.receiptAmount ?? 0).toLocaleString()}
                    </td>
                    <td data-label="USD" className="px-2 py-1.5 text-right font-medium tabular-nums">{fmtCurrency(item.usdEquivalent)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold text-gray-900">
              <tr>
                <td className="px-2 py-2" colSpan={ptaeLegend.order.length > 0 ? 6 : 5}>
                  Total (USD)
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{fmtCurrency(claim?.totalUsdAmount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
};

const ClaimReviewPane = ({ claim, reportClaim, activeItemId, onSelectItem, infectedItemIds }) => {
  if (!claim) {
    return (
      <div className="p-6 text-sm text-gray-500 italic">Claim details unavailable.</div>
    );
  }

  const isPerDiem = claim.claimType === 'PerDiemClaim';
  const flags = reportClaim?.reviewFlags || {};
  const flagEntries = Object.entries(flags);
  const splits = reportClaim?.splits || [];

  // Built from the rows themselves so the badges and the legend can never
  // disagree. `amount` is the per-line value each claim type contributes to its
  // total: M&IE + lodging for a per diem day, the USD figure for a receipt.
  const ptaeRows = isPerDiem
    ? (claim.dailyDetails || []).map(d => ({
        ptae: d.ptae,
        amount: (Number(d.totalMIE) || 0) + (Number(d.lodgingCost) || 0),
      }))
    : (claim.items || []).map(i => ({ ptae: i.ptae, amount: i.usdEquivalent }));
  const ptaeLegend = buildPtaeLegend(ptaeRows);

  return (
    <div className="p-4 sm:p-6">
      {isPerDiem
        ? <PerDiemBody claim={claim} reportClaim={reportClaim} ptaeLegend={ptaeLegend} />
        : <ExpenseBody
            claim={claim}
            reportClaim={reportClaim}
            activeItemId={activeItemId}
            onSelectItem={onSelectItem}
            infectedItemIds={infectedItemIds}
            ptaeLegend={ptaeLegend}
          />}

      {/* Funding - assigned on the claim before the report was generated. */}
      <div className="mt-5 pt-4 border-t border-gray-200">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
          PTA-E Allocation
        </p>

        {/* Funded line by line once, but the lines it named have been replaced
            by an edit. Said out loud: an empty column and a silent fall-through
            to the claim-level split is indistinguishable from "never
            allocated", which is exactly how this went unnoticed. */}
        {claim.linePtaeStale && (
          <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-900">
            This claim was funded line by line, but the line items have changed
            since - the per-line allocation no longer matches and needs redoing
            by the admin.
          </div>
        )}
        {ptaeLegend.order.length > 0 ? (
          <>
            {/* The legend for the badges above: each code spelled out once, with
                how many lines carry it and what they come to. */}
            <ul className="space-y-1">
              {ptaeLegend.order.map(entry => (
                <li key={entry.code} className="flex items-start justify-between gap-3 text-sm">
                  <span className="flex items-start gap-2 min-w-0">
                    <PtaeBadge letter={entry.letter} />
                    <span className="min-w-0">
                      <span className="font-mono text-gray-700 break-all">{entry.code}</span>
                      <span className="block text-xs text-gray-500">
                        {entry.lines} {isPerDiem ? 'day' : 'line item'}{entry.lines === 1 ? '' : 's'}
                      </span>
                    </span>
                  </span>
                  <span className="text-gray-900 whitespace-nowrap">{fmtCurrency(entry.amount)}</span>
                </li>
              ))}
            </ul>

            {/* The report carries its own copy of the split, taken at generation.
                If the two sets of codes no longer agree, the report is the one
                that will be paid - so say so rather than showing only the
                claim-level view and letting them look identical. */}
            {splits.length > 0 && (() => {
              const reportCodes = splits.map(sp => sp.ptae).filter(Boolean).sort();
              const lineCodes = ptaeLegend.order.map(e => e.code).sort();
              const differs = reportCodes.join('|') !== lineCodes.join('|');
              return differs ? (
                <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <p className="font-semibold text-amber-900">
                    The report's allocation differs from the claim's.
                  </p>
                  <ul className="mt-1 space-y-0.5 text-amber-800">
                    {splits.map(sp => (
                      <li key={sp.id || sp._id} className="flex justify-between gap-3">
                        <span className="font-mono break-all">{sp.ptae || '-'}</span>
                        <span className="whitespace-nowrap">{fmtCurrency(sp.amount)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-amber-700">
                    The report's figures above are what will be paid.
                  </p>
                </div>
              ) : null;
            })()}
          </>
        ) : splits.length > 0 ? (
          <ul className="space-y-1">
            {splits.map(split => (
              <li key={split.id || split._id} className="flex justify-between gap-3 text-sm">
                <span className="font-mono text-gray-700 break-all min-w-0">{split.ptae || '-'}</span>
                <span className="text-gray-900 whitespace-nowrap">{fmtCurrency(split.amount)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex justify-between gap-3 text-sm">
            <span className="font-mono text-gray-700 break-all min-w-0">{reportClaim?.ptae || '-'}</span>
            <span className="text-gray-900 whitespace-nowrap">
              {fmtCurrency(reportClaim?.allocatedAmount ?? reportClaim?.total)}
            </span>
          </div>
        )}
      </div>

      {/* Flags already on this claim, attributed - a director sees the auditor's
          concern rather than silently replacing it. */}
      {flagEntries.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-200">
          <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
            Flags
          </p>
          <ul className="space-y-2">
            {flagEntries.map(([role, flag]) => (
              <li key={role} className="p-3 bg-amber-50 border-l-4 border-amber-400 rounded">
                <p className="text-xs font-semibold text-amber-800">
{flag.byName || role.replace(/_/g, ' ')}
                  {flag.at ? ` · ${fmtDate(flag.at)}` : ''}
                </p>
                <p className="text-sm text-amber-900 mt-1 whitespace-pre-wrap">{flag.reason}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ClaimReviewPane;
